import {Queue} from '../misc/Queue';

const gracePeriod = 30;

type resolvFn<T> = (value: PromiseLike<T>) => void;
type rejectFn = (reason?: any) => void;

interface Item {
  readonly launcher: () => Promise<any>;
  readonly resolve: resolvFn<any>;
  readonly reject: rejectFn;
}

export class PromiseThrottler {
  private timings: number[];

  private queue: Queue<Item>;

  constructor(readonly monitorPeriod: number,
              readonly maxRequestsInMonitorPeriod: number) {
    this.timings = [];
    this.queue = new Queue<Item>();
  }

  private nextTime(now: number): number {
    if (this.canRunNow(now))
      return 0;
    let result = this.timings[0] + this.monitorPeriod;
    if (this.timings.length === this.maxRequestsInMonitorPeriod && result < now + gracePeriod)
      return 0;
    return result;
  }

  private canRunNow(now: number): boolean {
    if (this.timings.length > this.maxRequestsInMonitorPeriod)
      this.timings = this.timings.slice(this.timings.length - this.maxRequestsInMonitorPeriod);

    if (this.timings.length > 0 && this.timings[0] + this.monitorPeriod > now)
      this.timings = this.timings.filter((x) => x + this.monitorPeriod > now);

    return this.timings.length < this.maxRequestsInMonitorPeriod;
  }

  run<T>(action: () => Promise<T>): Promise<T> {
    let promise = new Promise<T>((resolve, reject) => {
      this.queue.enqueue({launcher: action, resolve: resolve, reject: reject});
    });

    this.runNext();

    return promise;
  }

  runNext = () => {
    const now = Date.now();

    let nextTime = 0;
    while (!this.queue.isEmpty && (nextTime = this.nextTime(now)) === 0)
      this.resolve(this.queue.dequeue());

    if (this.queue.isEmpty)
      return;
    setTimeout(this.runNext, nextTime - now);
  };

  private resolve = (item: Item | undefined) => {
    if (item === undefined)
      return;
    this.timings.push(Date.now());

    try {
      item.resolve(item.launcher());
    } catch (error) {
      item.reject(error);
    }
  };
}
